import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  keyframes,
  LinearProgress,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BoltIcon from '@mui/icons-material/Bolt';
import { useOptimization } from '../../context/OptimizationContext';

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

const flashBest = keyframes`
  0%, 100% {
    background-color: rgba(76, 175, 80, 0.1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  50% {
    background-color: rgba(76, 175, 80, 0.3);
    box-shadow: 0 0 12px 6px rgba(76, 175, 80, 0.6);
  }
`;

const EnergyLeaderboard: React.FC = () => {
  const { results, isRunning, completedAlgorithms } = useOptimization();

  const energyData = useMemo(() => {
    if (!results) return null;

    const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];

    // Get energy data for algorithms that have results (even if not fully completed)
    const energyEntries = algorithmKeys
      .map((key, index) => {
        const result = results[key];
        if (!result) return null;
        return {
          name: algorithmNames[index],
          key: algorithmKeys[index],
          energy: result.energy,
          isCompleted: completedAlgorithms.has(algorithmNames[index]),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => a.energy - b.energy); // Sort by energy (lower is better)

    return energyEntries;
  }, [results, completedAlgorithms]);

  // Don't show if no results and not running
  if (!energyData || energyData.length === 0) {
    if (!isRunning) {
      return null;
    }
    // Show placeholder while running but no results yet
    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          alignSelf: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BoltIcon sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6" component="h2">
            Energy Leaderboard
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          Waiting for algorithms to complete...
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Paper>
    );
  }

  const bestAlgorithm = energyData[0];
  const hasMultipleResults = energyData.length > 1;

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, md: 1.5 },
        alignSelf: 'flex-start',
        minWidth: { xs: '100%', md: 200 },
        maxWidth: { xs: '100%', md: 250 },
        width: { xs: '100%', md: 'auto' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <BoltIcon sx={{ mr: 0.5, color: 'warning.main', fontSize: 20 }} />
        <Typography variant="subtitle1" component="h2" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          Energy Leaderboard
        </Typography>
      </Box>

      {/* Best Algorithm Highlight */}
      {bestAlgorithm && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            border: '1.5px solid',
            borderColor: 'success.main',
            animation: `${flashBest} 2s ease-in-out infinite`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <EmojiEventsIcon
              sx={{
                color: 'warning.main',
                mr: 0.5,
                fontSize: 20,
                animation: `${pulse} 1.5s ease-in-out infinite`,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.85rem' }}>
              Best: {bestAlgorithm.name}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', fontFamily: 'monospace', fontSize: '1rem' }}>
            {bestAlgorithm.energy.toFixed(6)} J
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
            Lowest energy
          </Typography>
        </Box>
      )}

      {/* Leaderboard List */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
          Ranking (Lower is Better)
        </Typography>
        <List dense sx={{ p: 0 }}>
          {energyData.map((entry, index) => {
            const isBest = index === 0;
            const isWorst = index === energyData.length - 1 && hasMultipleResults;
            const rank = index + 1;

            return (
              <ListItem
                key={entry.key}
                sx={{
                  mb: 0.5,
                  borderRadius: 1,
                  backgroundColor: isBest
                    ? 'rgba(76, 175, 80, 0.08)'
                    : isWorst
                    ? 'rgba(211, 47, 47, 0.08)'
                    : 'transparent',
                  border: isBest ? '1px solid' : 'none',
                  borderColor: isBest ? 'success.main' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isBest
                      ? 'rgba(76, 175, 80, 0.15)'
                      : isWorst
                      ? 'rgba(211, 47, 47, 0.15)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Chip
                    label={rank}
                    size="small"
                    color={isBest ? 'success' : isWorst ? 'error' : 'default'}
                    sx={{
                      fontWeight: 700,
                      minWidth: 24,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isBest ? 700 : isWorst ? 600 : 500,
                          color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                        }}
                      >
                        {entry.name}
                      </Typography>
                      {!entry.isCompleted && isRunning && (
                        <Chip
                          label="Running"
                          size="small"
                          color="primary"
                          sx={{ height: 16, fontSize: '0.6rem', px: 0.5 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.secondary',
                        fontWeight: isBest ? 600 : 400,
                        fontSize: '0.75rem',
                      }}
                    >
                      {entry.energy.toFixed(6)} J
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Progress indicator if still running */}
      {isRunning && energyData.length < 6 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {energyData.length} of 6 algorithms completed
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(energyData.length / 6) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default EnergyLeaderboard;

